import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CheckIcon from "@mui/icons-material/Check";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Menu from "@mui/material/Menu";
import { SimpleTreeView } from "@mui/x-tree-view/SimpleTreeView";
import { TreeItem } from "@mui/x-tree-view/TreeItem";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import Organization from "../../../../../domain/entities/Organization";
import State, { StateStatus } from "../../../../../domain/entities/State";
import User from "../../../../../domain/entities/User";
import { useAuth } from "../../../../hooks/useAuth";
import "./OrganizationSelector.scss";

/**
 * OrganizationSelector Component
 * 
 * Displays a hierarchical tree view of organizations with expand/collapse functionality.
 * 
 * DEFENSIVE PROGRAMMING IMPLEMENTED (2025-11-08):
 * ============================================
 * This component has been hardened against data quality issues:
 * 
 * 1. REQUIRED FIELDS: Only 'id' and 'name' are required
 *    - Does NOT require 'description' (was causing orgs to be filtered out)
 *    - Validates both fields exist and are non-empty strings
 * 
 * 2. NULL SAFETY: All organization property access uses optional chaining
 *    - user?.organization?.id
 *    - org?.name, org?.id, org?.childOrganizations
 * 
 * 3. ARRAY VALIDATION: All array operations check for valid arrays first
 *    - Validates array exists before filtering/mapping/sorting
 *    - Returns empty array or null instead of crashing
 * 
 * 4. FILTERS: Top-level orgs filtered to ensure valid id and name
 *    - Prevents rendering broken organization entries
 * 
 * 5. SORTING: Safe sorting with fallback to empty strings
 *    - Handles missing/invalid name fields gracefully
 * 
 * 6. REACTIVE UPDATES: Listens to Redux state for organization changes
 *    - Automatically updates when organizations are added/deleted
 *    - Falls back to localStorage if Redux state not yet loaded
 * 
 * DO NOT REMOVE THESE SAFETY CHECKS - they prevent production crashes
 * from bad/incomplete organization data.
 */

const OrganizationSelector = () => {
  const { user, switchOrganization }: { user: User; switchOrganization: any } =
    useAuth()!;
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [expandedOrgs, setExpandedOrgs] = useState<string[]>([]);
  const open = Boolean(anchorEl);
  
  // Find all parent org IDs that contain the currently selected organization
  const findPathToOrg = (orgs: Organization[], targetId: string, path: string[] = []): string[] | null => {
    for (const org of orgs) {
      if (org.id === targetId) {
        return path;
      }
      if (org.childOrganizations && org.childOrganizations.length > 0) {
        const found = findPathToOrg(org.childOrganizations, targetId, [...path, org.id!]);
        if (found) return found;
      }
    }
    return null;
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    // When opening the menu, expand all parent orgs to show the current selection
    const allOrgsToExpand: string[] = [];
    newUserOrgs.forEach((userOrg, index) => {
      allOrgsToExpand.push(`HomeOrg${index}`);
      if (userOrg.organization.childOrganizations) {
        const pathToSelected = findPathToOrg(userOrg.organization.childOrganizations, user.organization.id!);
        if (pathToSelected) {
          allOrgsToExpand.push(...pathToSelected);
        }
      }
    });
    setExpandedOrgs(allOrgsToExpand);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };
  
  // Get user orgs from Redux store (updated when orgs are added/deleted)
  const userOrgsFromRedux = useSelector(
    (state: State) => state.administrator.userOrgs
  );
  const userOrgsLoadStatus = useSelector(
    (state: State) => state.administrator.userOrgsLoadStatus
  );
  
  const [newUserOrgs, setNewUserOrgs] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  let orgsArray: string[] = [];

  const getSelectedOrg = (selectedId: string) => {
    const findOrg = (orgs: Organization[]): Organization | null => {
      for (const org of orgs) {
        if (org.id === selectedId) return org;
        const child = findOrg(org.childOrganizations || []);
        if (child) return child;
      }
      return null;
    };

    // Search all user orgs (top-level and children) for the selected org
    for (const userOrg of newUserOrgs) {
      if (userOrg.organization.id === selectedId) {
        // Always keep current user identity, only swap organization
        return {
          ...user,
          organization: userOrg.organization,
          originalOrg: user.originalOrg || user.organization
        } as User;
      }
      const childOrg = findOrg(userOrg.organization.childOrganizations || []);
      if (childOrg) {
        return {
          ...user,
          organization: childOrg,
          originalOrg: user.originalOrg || user.organization
        } as User;
      }
    }

    return user;
  };

  const onOrganizationClick = (orgId: string) => {
    const selectedOrg = getSelectedOrg(orgId);
    switchOrganization(selectedOrg);
    //window.location.reload();
    window.location.href = "/";
  };

  /**
   * Recursively renders organization options as a tree structure.
   * 
   * DEFENSIVE PROGRAMMING:
   * - Validates organizations array exists and is an array
   * - Filters out invalid organizations (missing ID or name)
   * - Returns null for empty results instead of crashing
   * - Only requires 'id' and 'name' fields (NOT description)
   * 
   * @param organizations - Array of organizations to render
   * @param indent - Current indentation level (for styling)
   * @returns JSX elements for the tree items, or null if no valid organizations
   */
  const getOrganizationOptions = (
    organizations?: Organization[],
    indent = 1
  ): JSX.Element[] | null => {
    // Defensive: Return null if no organizations provided
    if (!organizations || !Array.isArray(organizations)) {
      return null;
    }

    // Filter out invalid organizations (must have valid ID and name)
    const validOrgs = organizations.filter(org => {
      const hasValidId = org?.id && typeof org.id === 'string' && org.id.trim() !== '';
      const hasValidName = org?.name && typeof org.name === 'string' && org.name.trim() !== '';
      return hasValidId && hasValidName;
    });

    // Return null if no valid organizations after filtering
    if (validOrgs.length === 0) {
      return null;
    }

    return validOrgs.map((org, indx) => {
      if (org.childOrganizations && org.childOrganizations.length > 0) {
        orgsArray.push(org.id!);
      }
      return (
        <React.Fragment key={`${org.id}_${indx}`}>
          <TreeItem
            itemId={`${org.id}`}
            label={
              <Button
                variant="text"
                size="small"
                onClick={() => onOrganizationClick(org.id!)}
              >
                {getItemLabel(org)}
              </Button>
            }
          >
            {org.childOrganizations && org.childOrganizations.length > 0 ? 
              getOrganizationOptions(
                sortByNameAsc(org.childOrganizations),
                indent + 1
              ) : null
            }
          </TreeItem>
        </React.Fragment>
      );
    });
  };

  const getItemLabel = (org: Organization) => {
    const isSelected = org.id === user?.organization?.id;
    return (
      <div style={{ 
        display: "flex", 
        alignItems: "center",
        fontWeight: isSelected ? "bold" : "normal",
        backgroundColor: isSelected ? "#e3f2fd" : "transparent",
        padding: "4px 8px",
        borderRadius: "4px",
        margin: "-4px -8px"
      }}>
        {org.name}
        {isSelected && (
          <CheckIcon sx={{ ml: 2 }} color="secondary" />
        )}
      </div>
    );
  };

  useEffect(() => {
    // Use Redux state as primary source (updates when orgs added/deleted)
    if (userOrgsFromRedux && Array.isArray(userOrgsFromRedux) && userOrgsFromRedux.length > 0) {
      setNewUserOrgs(userOrgsFromRedux);
      setIsLoading(false);
    } else if (userOrgsLoadStatus === StateStatus.LOADING) {
      // Still loading from backend
      setIsLoading(true);
    } else {
      // Fall back to localStorage if Redux state not yet loaded or failed
      const lsUserOrgs = localStorage.getItem("user-org");
      setNewUserOrgs(lsUserOrgs ? JSON.parse(lsUserOrgs) : []);
      setIsLoading(false);
    }
  }, [userOrgsFromRedux, userOrgsLoadStatus]);

  const sortByNameAsc = (org: Organization[]) => {
    // Defensive: Return empty array if invalid input
    if (!org || !Array.isArray(org)) {
      return [];
    }
    
    return org.sort((a, b) => {
      // Safely get names with fallback to empty string
      const nameA = (a?.name && typeof a.name === 'string') ? a.name.toLowerCase() : '';
      const nameB = (b?.name && typeof b.name === 'string') ? b.name.toLowerCase() : '';
      return nameA.localeCompare(nameB);
    });
  };

  return (
    <div>
      <Button
        id="basic-button"
        aria-controls={open ? "basic-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        onClick={handleClick}
        endIcon={isLoading ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <ArrowDropDownIcon />}
        variant="contained"
        color="secondary"
        fullWidth
      >
        {user?.organization?.name || 'Select Organization'}
      </Button>
      <Menu
        className="organization-selector"
        id="organization-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "basic-button",
        }}
        sx={{ maxHeight: "400px" }}
        color="secondary"
      >
        {isLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
            <CircularProgress size={24} color="secondary" />
          </div>
        ) : (
          <SimpleTreeView
            expandedItems={expandedOrgs}
            onExpandedItemsChange={(event, itemIds) => setExpandedOrgs(itemIds)}
            disableSelection={false}
            expansionTrigger={"iconContainer"}
          >
            {newUserOrgs?.filter(org => org?.organization?.id && org?.organization?.name).map((org, index) => {
              const isSelected = org.organization.id === user?.organization?.id;
              return (
              <React.Fragment key={`${index}_user_org_option`}>
                <TreeItem
                  itemId={`HomeOrg${index}`}
                  label={
                    <div style={{ 
                      display: "flex", 
                      alignItems: "center",
                      fontWeight: isSelected ? "bold" : "normal",
                      backgroundColor: isSelected ? "#e3f2fd" : "transparent",
                      padding: "4px 8px",
                      borderRadius: "4px",
                      margin: "-4px -8px"
                    }}>
                      <Button
                        variant="text"
                        size="small"
                        color="secondary"
                        onClick={() => onOrganizationClick(org?.organization.id!)}
                        sx={{ fontWeight: isSelected ? "bold" : "normal" }}
                      >
                        {org?.organization.name}
                      </Button>
                      {isSelected && (
                        <CheckIcon sx={{ ml: 2 }} color="secondary" />
                      )}
                    </div>
                  }
                >
                  {org.organization.childOrganizations && org.organization.childOrganizations.length > 0 ? (
                    getOrganizationOptions(
                      sortByNameAsc(org.organization.childOrganizations)
                    )
                  ) : null}
                </TreeItem>
              </React.Fragment>
              );
            })}
          </SimpleTreeView>
        )}
      </Menu>
    </div>
  );
};

export default OrganizationSelector;
